import type { FC } from "react";
import { useDebug } from "@/context/DebugContext";
import type { BaseComponentProps } from "@/types/component-props";
import { cn } from "@/utils/cn";

export interface DebugToggleGroupProps extends BaseComponentProps {}

/**
 * DebugToggleGroup
 *
 * A single toggle button for enabling/disabling debug mode.
 * Uses aria-pressed for proper accessibility semantics.
 */
export const DebugToggleGroup: FC<DebugToggleGroupProps> = ({ className }) => {
  const { debugMode, toggleDebugMode } = useDebug();
  const isOn = debugMode === "on";

  return (
    <div
      className={cn(
        "inline-flex items-center justify-center p-0.5 h-10 rounded-md",
        "bg-gray-50/95 dark:bg-gray-800/95",
        "border-gray-200 dark:border-gray-700 border",
        "shadow-[0_1px_2px_0_rgba(0,0,0,0.03)] dark:shadow-sm",
        className,
      )}
    >
      <button
        aria-pressed={isOn}
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium mx-0.5",
          "cursor-pointer",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary/40 dark:focus-visible:ring-theme-primary/40",
          "data-[state=off]:opacity-50 data-[state=off]:hover:opacity-100",
          "data-[state=on]:bg-theme-primary/10 dark:data-[state=on]:bg-gray-900",
          "hover:bg-theme-muted/70",
        )}
        data-state={isOn ? "on" : "off"}
        onClick={toggleDebugMode}
        type="button"
      >
        Debug
      </button>
    </div>
  );
};
